import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, debounce, FormControl, FormControlLabel, Grid, InputAdornment, InputLabel, LinearProgress, MenuItem, Select, Slider, Stack, Switch, TextField, Tooltip } from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { ArrowCircleUp, AutoAwesome, Clear, EditSquare, Download, Height, HighlightAlt, HighlightOff, Info, MoreVert, Save, Terrain, Tune, Visibility } from '@mui/icons-material';
import * as htmlToImage from 'html-to-image';
import { useUser } from '../../store/User';
import Libre from '../Libre';
import Jobs from '../Jobs';
import Aois from '../Aois';
import Terrabase from '../Terrabase';
import { apiUrl, tileUrl } from '../../api';
import { fovOptions } from '../Util';

import tacticalData from '../../data/tactical.json';
import worldData from '../../data/world.json';

import './index.css';

const coordErrorText = "Invalid Coordinate Format.";
const nameErrorText = "Invalid or Duplicate AOI Name.";

const Map = () => {
    const { aoi, mapConfig, mapContext, occupancy, sources,user, setAOI, setAOIs, setImagery, setMapConfig, setOccupancy, setShowLogin, setSources, setTerraBase, setTerraBases, setTacticalTerraBase, setWorldTerraBase, setUser } = useUser();
    const mapRef = useRef(null);

    const savedView = JSON.parse(localStorage.getItem('terra-view'));

    const [showPoints, setShowPoints] = useState(false);
    const [showSidebar, setShowSidebar] = useState(savedView?.sidebar ? savedView.sidebar : false);
    const [nameError, showNameError] = useState(false);
    const [coordError, showCoordError] = useState(false);
    const [offset, setOffset] = useState({x: 100, y: 100})
    // const [threeD, setThreeD] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [showAI, setShowAI] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [aiQuestion, setAIQuestion] = useState("");
    const [conversation, setConversation] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            if(user?.role?.includes("sales")) {
                getTacticalTerrabaseData();
                getWorldTerrabaseData();
                setShowSidebar(false);
                setMapConfig({
                    ...mapConfig,
                    world: true,
                    tactical: true,
                    mgrs: true,
                    drawPolygons: true
                });
            } else {
                getAOIs();
                getTerraBaseData();
                getImagerySources();
            }
        }
    }, [user?.role]);

    useEffect(() => {
        if (aoi && mapRef.current) {
            const rect = mapRef.current.getBoundingClientRect();
            setOffset({
                x: rect.width / 2,
                y: rect.height / 2
            });
        }
    }, [aoi]);

    const getWorldTerrabaseData = async () => {
        try {
            const results = worldData;
            setWorldTerraBase(results);
        } catch (error) {
            console.log("Error fetching world terrabase data:", error);
        }
    }

    const getTacticalTerrabaseData = async () => {
        try {
            const results = tacticalData;
            setTacticalTerraBase(results);
        } catch (error) {
            console.log("Error fetching tactical terrabase data:", error);
        }
    }

    const getAOIs = async () => {
        try {
            const response = await fetch(`${apiUrl}/aois/all`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('terra-token')}`
                }
            });
            if (!response.ok) throw new Error("Fetch failed");
            
            const results = await response.json();
            
            setAOIs(results);
        } catch (error) {
            console.log("Error fetching aois:", error);
        }
    }

    const getTerraBaseData = async () => {
        try {
            const response = await fetch(`${apiUrl}/terrabases/all`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('terra-token')}`
                },
            });
            const resData = await response.json();

            if (response.ok) {
                setTerraBases(resData);
            } else if(response.status === 401) {
                setUser(null);
                setShowLogin(true);
            }
        } catch (error) {
            console.log("Error fetching TerraBase data:", error);
        }
    }

    const getImagerySources = async () => {
        try {
            const response = await fetch(`${apiUrl}/imagery/sources`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('terra-token')}`
                },
            });
            const resData = await response.json();

            if (response.ok) {
                setSources(resData);
            } else if(response.status === 401) {
                setUser(null);
                setShowLogin(true);
            }
        } catch (error) {
            console.log("Error fetching Imagery sources:", error);
        }
    }


    const postTerraBaseData = async (altitudeRange) => {
        try {
            const response = await fetch(`${apiUrl}/terrabases/tactical`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('terra-token')}`
                },
                body: JSON.stringify({altitude_range: altitudeRange}),
            });
            const resData = await response.json();

            if (response.ok) {
                setTerraBases(resData);
            } else if(response.status === 401) {
                setUser(null);
                setShowLogin(true);
            }
        } catch (error) {
            console.log("Error fetching TerraBase data:", error);
        }
    }

    const setSidebar = (value) => {
        setShowSidebar(value);
        const view = savedView || {};
        view.sidebar = value;
        localStorage.setItem('terra-view', JSON.stringify(view));
    }

    const addToAOIs = async () => {
        const extension = aoi.id ? "update" : "create";
        const response = await fetch(`${apiUrl}/aois/${extension}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('terra-token')}`
          },
          body: JSON.stringify(aoi),
        });

        const result = await response.json();

        if (response.ok) {
            setAOIs(result);
        } else if(response.status === 401) {
            setUser(null);
            setShowLogin(true);
        }
        setAOI(null);
    }

    const handleEditPolygons = () => {
        const draw = !mapConfig.drawPolygons;
        if (draw) {
            setShowFilters(false);
        } else {
            setAOI(null);
        }

        setMapConfig({...mapConfig,
            drawPolygons: draw,
            aois: true
        });
    }

    const handleEditFilters = () => {
        const filters = !showFilters;
        if (filters) {
            setMapConfig({...mapConfig, drawPolygons: false});
            setShowAI(false);
            setShowInfo(false);
        }
        setShowFilters(filters);
    }

    const handleInfo = () => {
        const info = !showInfo;
        if(info)
            setShowFilters(false);

        setShowInfo(info);
    }

    const handleRangeChange = (event, newValue) => {
        setMapConfig({...mapConfig, altitudeRange: newValue});
    }

    const handleDownloadTiles = async () => {
        if(mapContext) {
            const bounds = mapContext.getBounds();
            const southWest = bounds.getSouthWest();
            const northEast = bounds.getNorthEast();

            // const response = await fetch(`${tileUrl}/download_all_tiles`, {
            //     method: 'POST',
            //     headers: {
            //         'Content-Type': 'application/json',
            //     },
            //     body: JSON.stringify({
            //         w: southWest.lng,
            //         s: southWest.lat,
            //         e: northEast.lng,
            //         n: northEast.lat
            //     })
            // });

            // if (response.ok) {
            //     // Convert response to a Blob (binary data)
            //     const blob = await response.blob(); 
            //     const url = window.URL.createObjectURL(blob);
                
            //     // Create a temporary link to trigger the download
            //     const a = document.createElement('a');
            //     a.href = url;
            //     a.download = `tiles.zip`; 
            //     document.body.appendChild(a);
            //     a.click();
                
            //     // Cleanup
            //     window.URL.revokeObjectURL(url);
            //     document.body.removeChild(a);
            // } else {
            //     // Log the error body to see exactly why the server sent a 422
            //     const errorData = await response.json();
            //     console.error('Download failed:', errorData);
            // }
        }
    }

    const editAOIContainer = () => {
        return (
            <Grid container spacing={2}>
                <Grid size={12} sx={{ alignItems: "center",  display: 'flex' }}>
                    <Tooltip title="Deselect AOI" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                        <IndeterminateCheckBoxIcon sx={{ color: 'white', mr: 1 }} className='add-aoi' onClick={() => setAOI(null)} />
                    </Tooltip>
                    <TextField 
                        label="AOI Name" 
                        sx={{ width: 270 }} 
                        variant="outlined" 
                        size="small"
                        value={ aoi?.name || '' }
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) => setAOIName(e.target.value)}
                        error={nameError}
                        helperText={nameError ? nameErrorText : ""}

                        slotProps={{
                        input: {
                            endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Save AOI" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                                <Save sx={{ color: '#4c4c4c', ml: 1 }} 
                                    className={ nameError || coordError ? '' : 'save-aoi' } 
                                    onClick={() => addToAOIs()}/>
                                </Tooltip>
                            </InputAdornment>
                            ),
                        },
                        }}
                    />
                    <Tooltip title="Show Points" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                        <MoreVert sx={{ color: '#fff', cursor: 'pointer' }} onClick={() => setShowPoints(!showPoints)}/>
                    </Tooltip>
                </Grid>
                { showPoints &&
                    <Grid className="aoi-list" container size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    { aoi?.bounds?.coordinates && aoi.bounds.coordinates[0].length > 0 &&
                        aoi.bounds.coordinates[0].map((point, index) => (
                            <Grid  size={12} key={`point-${index}`} container spacing={1} sx={{ alignItems: "center"}}>
                                <Grid size={5}>
                                    <TextField 
                                        label={`Point ${index + 1} Latitude`} 
                                        sx={{ width: '100%' }} 
                                        variant="outlined" 
                                        size="small"
                                        value={`${point[1]}`}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            const lat = parseFloat(e.target.value);
                                            if(!isNaN(lat)) {
                                                const newPoints = [...aoi.bounds.coordinates[0]];
                                                newPoints[index] = [newPoints[index][0], lat];
                                                setAOI({...aoi, bounds: {...aoi.bounds, coordinates: [newPoints]}});
                                                showCoordError(false);
                                            } else {
                                            showCoordError(true);
                                            }
                                        }}

                                        error={coordError}
                                        helperText={coordError ? coordErrorText : ""}
                                    />
                                </Grid>
                                <Grid size={5}>
                                    <TextField 
                                    label={`Point ${index + 1} Longitude`} 
                                    sx={{ width: '100%' }} 
                                    variant="outlined" 
                                    size="small"
                                    value={`${point[0]}`}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        const lng = parseFloat(e.target.value);
                                        if(!isNaN(lng)) {
                                            const newPoints = [...aoi.bounds.coordinates[0]];
                                            newPoints[index] = [lng, newPoints[index][1]];
                                            setAOI({...aoi, bounds: {...aoi.bounds, coordinates: [newPoints]}});
                                            showCoordError(false);
                                        } else {
                                            showCoordError(true);
                                        }
                                    }}
                                    error={coordError}
                                    helperText={coordError ? coordErrorText : ""}
                                    />
                                </Grid>
                                <Grid size={2}  sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Tooltip title="Remove Point" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                                        <HighlightOff sx={{ color: 'white', cursor: 'pointer', '&:hover': { color: '#ff0000 !important'}, }} onClick={() => {
                                            const newPoints = [...aoi.bounds.coordinates[0]];
                                            newPoints.splice(index, 1);
                                            setAOI({...aoi, bounds: {...aoi.bounds, coordinates: [newPoints]}});
                                        }}/>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        ))
                    }                     
    {/* { aoi?.mgrs && Object.keys(aoi.mgrs).length > 0 &&
                        Object.keys(aoi.mgrs).map((mgrsGrid, index) => (
                            <Grid size={12} key={`${mgrsGrid}-${index}`} container spacing={1} sx={{ alignItems: "center"}}>
                                <Grid size={10}>
                                    {mgrsGrid}
                                </Grid>
                                <Grid size={2}  sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Tooltip title="Remove Point">
                                    <RemoveCircle sx={{ color: 'white', cursor: 'pointer', '&:hover': { color: '#ff0000 !important'}, }} onClick={() => {
                                        const newGrids = {...aoi.mgrs};
                                        delete newGrids[mgrsGrid];
                                        setAOI({...aoi, mgrs: newGrids});
                                    }}/>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        ))
                    } */}
                    </Grid>   
                }
            </Grid>
        );
    };

    const setAOIName = (name) => {
        setAOI({...aoi, name: name});

        const userAOIs = user?.aois || {};
        if (name.length === 0 || Object.keys(userAOIs).includes(name)) {
            showNameError(true);
        } else {
            showNameError(false);
        }
    }

    const handleMouseDown = (e) => {
        const startX = e.clientX - offset.x;
        const startY = e.clientY - offset.y;

        const handleMouseMove = (moveEvent) => {
            setOffset({
                x: moveEvent.clientX - startX,
                y: moveEvent.clientY - startY
            });
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container >

                <Grid ref={mapRef} size={(user && showSidebar) ? 8 : 12} className="page">
                    { user &&
                        <>
                            <Box className="edit-box" sx={{ display: 'flex', flexDirection: 'column'}}>
                                <Stack direction="column" spacing={1} alignItems="center">
                                    { !user?.role?.includes("sales") &&
                                        <>
                                            <Tooltip title="Edit AOIs" placement="left">
                                                <EditSquare className={mapConfig.drawPolygons ? "edit-button active" : "edit-button"} onClick={() => handleEditPolygons()} style={{cursor: "pointer"}} />
                                            </Tooltip>
                                            <Tooltip title="Filters" placement="left">
                                                <Tune className={showFilters ? "edit-button active" : "edit-button"} onClick={handleEditFilters} style={{cursor: "pointer"}} />
                                            </Tooltip>
                                            <Tooltip title="Info" placement="left">
                                                <Info className={showInfo ? "edit-button active" : "edit-button"} onClick={handleInfo} style={{cursor: "pointer"}} />
                                            </Tooltip>
                                        </>
                                    }
                                    {/* { threeD &&
                                        <Tooltip title="Terrain" placement="left">
                                            <Terrain className={mapConfig.terrain ? "edit-button active" : "edit-button"} onClick={() => setMapConfig({...mapConfig, terrain: !mapConfig.terrain})} style={{cursor: "pointer"}} />
                                        </Tooltip>
                                    } */}

                                    {/* <Tooltip title="Altitude" placement="left">
                                        <Height className={showInfo ? "edit-button active" : "edit-button"} onClick={handleInfo} style={{cursor: "pointer"}} />
                                    </Tooltip> */}
                                    
                                    {/* <Tooltip title="Download Tiles">
                                        <Download className={"edit-button"} onClick={() => handleDownloadTiles()} style={{cursor: "pointer"}} />
                                    </Tooltip> */}
                                </Stack>
                            </Box>
                            { (showAI || showFilters || showInfo) &&
                            <Grid className="edit-container" container spacing={2} padding={3} alignItems="center">
                                { showFilters &&
                                    <>
                                        <Grid size={12}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id={`fov-label`}>
                                                    FIELD OF VIEW
                                                </InputLabel>
                                                <Select
                                                    labelId={`fov-label`}
                                                    label="FIELD OF VIEW"
                                                    variant="outlined"
                                                    size="small"
                                                    value={mapConfig["fov"]}
                                                    onChange={(e) => setMapConfig({...mapConfig, 'fov': e.target.value})}
                                                    sx={{ width: '100%'}}
                                                >
                                                    {fovOptions.map((fov) => (
                                                        <MenuItem key={`fov-option-${fov}`} value={fov}>
                                                            {fov === 50 ? `${fov} - Default`: fov}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={12}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id={`imagery-source-label`}>
                                                    IMAGERY SOURCE
                                                </InputLabel>
                                                <Select
                                                    labelId={`imagery-source-label`}
                                                    label="IMAGERY SOURCE"
                                                    variant="outlined"
                                                    size="small"
                                                    value={mapConfig["source"]}
                                                    onChange={(e) => setMapConfig({...mapConfig, 'source': e.target.value})}
                                                    sx={{ width: '100%'}}
                                                >
                                                    {sources.map((source) => (
                                                        <MenuItem key={`source-option-${source}`} value={source}>
                                                            {source}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={12} sx={{ alignItems: "center"}}>
                                            <p>ALTITUDE RANGE</p>
                                            <Slider
                                                id="altitude-range"
                                                value={mapConfig.altitudeRange}
                                                onChange={handleRangeChange}
                                                valueLabelDisplay="auto"
                                                step={1000}
                                                min={0}
                                                max={30000}
                                                marks  
                                            />
                                        </Grid>
                                    </>
                                }
                                { showInfo &&
                                    <>
                                        <p><b>Edit Instructions:</b></p>
                                        <ul>
                                            <li>To create AOIs click the top Edit AOIs icon & confirm the button is green</li>
                                            <li>Click creates / adds a point to a polygon</li>
                                            <li>Shift + Mousemove creates a rectangle polygon after first point is created</li>
                                            <li>Click + Drag circle markers to update AOI polygon</li>
                                            <li>Delete || Backspace removes the last polygon point</li>
                                        </ul>
                                    </>
                                }
                                {/* { mapConfig.drawPolygons &&
                                    <Grid size={12} sx={{ alignItems: "center"}}>
                                        POLY
                                        <Switch checked={mapConfig.mgrs} onChange={() => setMapConfig({...mapConfig, mgrs: !mapConfig.mgrs})} color="primary" size='small' />
                                        MGRS
                                    </Grid>
                                } */}
                            </Grid>
                            }
                            { !user?.role &&
                                <div className="map-controller top" style={{flexGrow: 1}}>
                                { showSidebar ?
                                    <KeyboardDoubleArrowLeftIcon className="sidebar-button" onClick={() => setSidebar(false)} />
                                    :
                                    <KeyboardDoubleArrowRightIcon className="sidebar-button" onClick={() => setSidebar(true)} />
                                }
                                </div>
                            }
                            <div className="map-controller bottom">
                                { user?.role && user.role.includes("sales") ?
                                    <>
                                        <FormControlLabel control={
                                            <Switch checked={mapConfig.world} onChange={() => setMapConfig({...mapConfig, world: !mapConfig.world})} color="primary" size='small' />
                                        } label="World TerraBase" />
                                        <FormControlLabel control={
                                            <Switch checked={mapConfig.tactical} onChange={() => setMapConfig({...mapConfig, tactical: !mapConfig.tactical})} color="primary" size='small' />
                                        } label="Tactical TerraBase" />
                                    </>
                                :
                                    <>
                                        <FormControlLabel control={
                                            <Switch checked={mapConfig.terraBases} onChange={() => setMapConfig({...mapConfig, terraBases: !mapConfig.terraBases})} color="primary" size='small' />
                                        } label="TerraBase" />

                                        <FormControlLabel control={
                                            <Switch checked={mapConfig.imagery} onChange={() => setMapConfig({...mapConfig, imagery: !mapConfig.imagery})} color="primary" size='small' />
                                        } label="Imagery" />

                                        <FormControlLabel control={
                                            <Switch checked={mapConfig.aois} onChange={() => setMapConfig({...mapConfig, aois: !mapConfig.aois})} color="primary" size='small' />
                                        } label="AOIs" />

                                        {/* <FormControlLabel control={
                                            <Switch checked={mapConfig.mgrs} onChange={() => setMapConfig({...mapConfig, mgrs: !mapConfig.mgrs})} color="primary" size='small' />
                                        } label="MGRS" /> */}
                                    </>
                                }
                                <FormControlLabel control={
                                    <Switch checked={mapConfig.occupancy} onChange={() => setMapConfig({...mapConfig, occupancy: !mapConfig.occupancy})} color="primary" size='small' />
                                } label="Recognition" />
                                {/* <FormControlLabel control={
                                    <Switch checked={threeD} onChange={() => setThreeD(!threeD)} color="primary" size='small' />
                                } label="3D" /> */}
                            </div>
                        </>
                    }

                    {/* { mapConfig.drawPolygons && aoi &&
                        <div className="edit-aoi-container"
                            onMouseDown={handleMouseDown}
                            style={{
                                top: `${offset.y}px`, 
                                left: `${offset.x}px`,
                                cursor: 'grab',
                                userSelect: 'none'
                            }}
                        >
                            { editAOIContainer() }
                        </div>
                    } */}

                    {/* { threeD ? */}
                        <Libre sidebar={showSidebar} />
                    {/* :
                         <LeafletMap sidebar={showSidebar} />
                    } */}               
                </Grid>
            </Grid>
        </Box>
    );
}

export default Map;